import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AgeGroupSelector from '../components/AgeGroupSelector';
import CharacterSettingForm from '../components/CharacterSettingForm';
import StoryPreview from '../components/StoryPreview';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { generateStory, regenerateScene } from '../services/story-generator';
import { addImagesToStory } from '../services/image-service';
import { saveStory } from '../services/firebase-stories';
import { validateStoryForm } from '../utils/validators';
import './CreateStoryPage.css';

const CreateStoryPage = () => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    ageGroup: 'preschoolers-older', // Default age group
    character: '',
    setting: ''
  });
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [story, setStory] = useState(null);
  
  // Handle age group selection change
  const handleAgeGroupChange = (ageGroup) => {
    setFormData(prev => ({ ...prev, ageGroup }));
  };
  
  // Handle character and setting changes
  const handleFormChange = ({ character, setting }) => {
    setFormData(prev => ({
      ...prev,
      ...(character !== undefined ? { character } : {}),
      ...(setting !== undefined ? { setting } : {})
    }));
  };
  
  // Generate a new story
  const handleGenerateStory = async () => {
    // Validate form inputs
    const validation = validateStoryForm(formData);
    
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }
    
    try {
      // Clear previous errors and set loading state
      setError(null);
      setValidationErrors({});
      setLoading(true);
      
      // Generate story text
      console.log('Generating story with:', formData);
      const generatedStory = await generateStory(
        formData.ageGroup,
        formData.character,
        formData.setting
      );
      
      // Add images to the story
      console.log('Adding images to story...');
      const storyWithImages = await addImagesToStory(generatedStory);
      
      // Update state with the complete story
      setStory(storyWithImages);
      
      console.log('Story generated successfully:', storyWithImages);
    } catch (err) {
      console.error('Error generating story:', err);
      setError({
        message: 'Could not create your story',
        details: err.message
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Regenerate the whole story
  const handleRegenerateStory = () => {
    handleGenerateStory();
  };
  
  // Regenerate a specific scene
  const handleRegenerateScene = async (sceneIndex) => {
    if (!story) return;
    
    try {
      setError(null);
      
      // Only set loading true for the specific scene
      // (would need a more complex loading state to show per-scene loading)
      setLoading(true);
      
      // Regenerate the scene
      console.log(`Regenerating scene ${sceneIndex}...`);
      const updatedStory = await regenerateScene(story, sceneIndex);
      
      // Add images to the scene
      const storyWithImages = await addImagesToStory(updatedStory);
      
      // Update state with the modified story
      setStory(storyWithImages);
      
      console.log('Scene regenerated successfully');
    } catch (err) {
      console.error('Error regenerating scene:', err);
      setError({
        message: 'Could not regenerate scene',
        details: err.message
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Save story to Firebase
  const handleSaveStory = async () => {
    if (!story) return;
    
    try {
      setError(null);
      setLoading(true);
      
      // Save story to Firebase
      console.log('Saving story to library...');
      const storyId = await saveStory(story);
      
      // Create a new story object with the ID
      const savedStory = {
        ...story,
        id: storyId
      };
      
      // Update local state
      setStory(savedStory);
      
      console.log('Story saved successfully with ID:', storyId);
      
      // Optional: Navigate to the story view page
      // navigate(`/story/${storyId}`);
    } catch (err) {
      console.error('Error saving story:', err);
      setError({
        message: 'Could not save your story',
        details: err.message
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Dismiss error message
  const handleDismissError = () => {
    setError(null);
  };
  
  return (
    <div className="create-story-page">
      <h1 className="page-title">Create Your <em>Magical Tale</em></h1>
      
      {/* Show error if exists */}
      {error && (
        <ErrorMessage 
          message={error.message} 
          details={error.details}
          onDismiss={handleDismissError}
        />
      )}
      
      <div className="create-story-content">
        {/* Left side: Input form */}
        <div className="story-form-container">
          <div className="form-header">
            <div className="eyebrow">Story Generator</div>
            <h2>Design Your Fairy Tale</h2>
          </div>
          
          {/* Age group selector */}
          <div className="form-section">
            <label className="form-section-label">Age Group</label>
            <AgeGroupSelector 
              selectedAgeGroup={formData.ageGroup} 
              onChange={handleAgeGroupChange}
            />
          </div>
          
          {/* Character and setting form */}
          <CharacterSettingForm 
            character={formData.character}
            setting={formData.setting}
            onChange={handleFormChange}
            isDisabled={loading}
          />
          
          {/* Form actions */}
          <div className="form-actions">
            <button 
              className="pill-button primary"
              onClick={handleGenerateStory}
              disabled={loading}
            >
              ✨ {story ? 'Regenerate Story' : 'Generate Story'}
            </button>
          </div>
          
          {/* Loading indicator for the form area */}
          {loading && !story && (
            <div className="form-loading">
              <LoadingSpinner message="Creating your magical tale..." />
            </div>
          )}
        </div>
        
        {/* Right side: Story preview */}
        <div className="story-preview-wrapper">
          <StoryPreview 
            story={story}
            loading={loading && story} // Only show loading here if story exists
            onSave={handleSaveStory}
            onRegenerate={handleRegenerateStory}
            onRegenerateScene={handleRegenerateScene}
            saved={story && story.id ? true : false}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateStoryPage;
